import { Injectable, inject } from '@angular/core';
import { enregistrerErreurTelemetrie } from '@services/core/telemetrie';
import { Entity } from '@entities/entity.class';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { db } from '@database/db';
import { ConnexionService } from '@services/core/connexion.service';
import { firstValueFrom } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';

import { catchError, throwError } from 'rxjs';
import {
	MatSnackBar,
	MatSnackBarAction,
	MatSnackBarActions,
	MatSnackBarLabel,
	MatSnackBarRef,
  } from '@angular/material/snack-bar';

//On importe toutes les entités pour associéer aux classes
import { RapportInvestigation } from '@entities/rapport-investigation.class';
import { Rapport } from '@entities/rapport.class';
import { PhraseDommage } from '@entities/phrase-dommage.class';
import { Photo } from '@entities/photo.class';
import { WaitingTransaction } from '@entities/waiting-transaction.class';
import { PhraseCategory } from '@entities/phrase-category.class';
import { Phrase } from '@entities/phrase.class';
import { Dommage } from '@entities/dommage.class';
import { Address } from '@entities/address.class';
import { Bi } from '@entities/bi.class';
import { Client } from '@entities/client.class';
import { PhraseRapport } from '@entities/phrase-rapport.class';
import { PhraseVersion } from '@entities/phrase-version.class';
import { QuestionRapport } from '@entities/question-rapport.class';
import { Question } from '@entities/question.class';
import { TdgPiece } from '@entities/tdg-piece.class';
import { TdgSupport } from '@entities/tdg-support.class';
import { User } from '@entities/user.class';


import { ActionOmbre, ombreDossier } from '@services/core/ombre-dossier';
@Injectable({
	providedIn: 'root'
})
export class DynamicService<T extends Entity> {
	// protected entityName:string = (this.constructor as any).name.replace('Service', '').replace('_', '');
	protected entityName:string = '';
	private _snackBar = inject(MatSnackBar);

	private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);

	// Option to skip IndexedDB writes for redundant tables
	// When true, the service will write to waitingtransaction but skip IndexedDB table writes  
	// This allows keeping existing code while transitioning to a simplified architecture
	protected skipIndexedDbIfMissing: boolean = false;

	constructor(
		protected http: HttpClient,
		protected connexionService: ConnexionService
	) { }

	forceEntityName(entityName: string) {
		this.entityName = entityName;
	}

	// Map entity names to their classes here
	private entityClassMap: { [key: string]: any } = {
		Entity,
		RapportInvestigation,
		Rapport,
		PhraseDommage,
		Photo,
		WaitingTransaction,
		PhraseCategory,
		Phrase,
		Dommage,
		Address,
		Bi,
		Client,
		PhraseRapport,
		PhraseVersion,
		QuestionRapport,
		Question,
		TdgPiece,
		TdgSupport,
		User
	};

	assignClass(entity: T): T {
		const className = this.entityName;
		const entityClass = this.entityClassMap[className];
		if (entityClass && typeof entityClass === 'function') {
			Object.setPrototypeOf(entity, entityClass.prototype);
		}
		return entity;
	}

	/**
	 * Dossiers ayant des modifications dans la file d'envoi. Leur copie locale ne doit pas
	 * être remplacée par la copie serveur, qui ne les contient pas encore : le technicien ne
	 * les voyait plus (phrases créées hors ligne disparues puis recréées, réponses à refaire).
	 * Mesuré : 137 écrasements en 2 jours chez 52 personnes (2.1.39). Elle sera remplacée au
	 * rechargement suivant, une fois la file vidée.
	 */
	async dossiersAvecModificationsEnAttente(): Promise<Set<number>> {
		const biIds = new Set<number>();
		try {
			await db.table('waitingtransaction').each((t: any) => {
				if (t?.biId && (t.tries ?? 0) < 10) biIds.add(Number(t.biId));
			});
		} catch (erreur) {
			console.warn('⚠️ File d\'envoi illisible, copies locales remplacées comme avant :', erreur);
		}
		return biIds;
	}

	/** Référentiels déjà rafraîchis depuis le serveur pendant cette session. */
	private static referentielsRafraichis = new Set<string>();

	/**
	 * Référentiel (pièces, supports…) lu LOCALEMENT, sans attendre le réseau. Téléchargé
	 * seulement si la table locale est vide (premier lancement) ; sinon rafraîchi une fois
	 * par session, en arrière-plan.
	 *
	 * Les écrans appelaient `getEntities(false)` en croyant désactiver la synchro — or le
	 * premier paramètre est `minimal` : chaque ouverture du tableau de dégâts, du wizard…
	 * retéléchargeait le référentiel et l'ATTENDAIT (5 400 fois par jour pour les supports).
	 */
	async getReferentiel(): Promise<T[]> {
		const locales = await this.getEntities(false, false);
		if (locales.length === 0) {
			return this.getEntities(false, true);
		}
		const cle = this.entityName;
		if (!DynamicService.referentielsRafraichis.has(cle)) {
			DynamicService.referentielsRafraichis.add(cle);
			if (await firstValueFrom(this.connexionService.isOnline())) {
				void this.sync(0).catch(() => undefined);
			}
		}
		return locales;
	}

	async getEntities(minimal:boolean = false, sync:boolean = true): Promise<T[]> {
		if(await firstValueFrom(this.connexionService.isOnline()) && sync) {
			const syncResult = await this.sync(0, minimal);
			// Si skipIndexedDbIfMissing, sync retourne directement les données de l'API
			if (this.skipIndexedDbIfMissing && Array.isArray(syncResult)) {
				return syncResult;
			}
		}
		const dbentity: string = this.entityName.toLowerCase();
		const thetable = db.table(dbentity);

		let results = await thetable.toArray();
		// if(await firstValueFrom(this.connexionService.isOnline()) && !sync && results.length === 0) {
		// 	// If we are offline and the table is empty, we try to sync
		// 	await this.sync(0, minimal);
		// 	results = await thetable.toArray();
		// }
		return results;
	}

	async filterEntities(filter: any, minimal:boolean = false, sync:boolean = true): Promise<T[]> {
		if(sync && await firstValueFrom(this.connexionService.isOnline())) {
			return await this.syncFilter(filter, minimal);
		}
		const dbentity: string = this.entityName.toLowerCase();
		const thetable = db.table(dbentity);
		return thetable.filter((T) => {
			return Object.keys(filter).every((key) => {	
				console.log(key, filter[key], T[key]);
				if(Array.isArray(filter[key])) {
					return filter[key].includes(T[key]);
				}
				if(filter[key] === 'NULL') {
					return T[key] === null || T[key] === '';
				}
				if(filter[key] === 'NOTNULL') {
					return T[key] !== null && T[key] !== '';
				}
				if(filter[key] instanceof Date) {
					return new Date(T[key]).getTime() === filter[key].getTime();
				}
				return T[key] === filter[key];
			});
		}).toArray();
	}

	async getEntity(id:number, sync:boolean = true): Promise<T> {
		if(sync && await firstValueFrom(this.connexionService.isOnline())) {
			const syncResult = await this.sync(id);
			if (this.skipIndexedDbIfMissing && syncResult && syncResult !== true && !Array.isArray(syncResult)) {
				return this.assignClass(syncResult as T);
			}
		}
		const dbentity: string = this.entityName.toLowerCase();
		return db.table(dbentity).get(id);
	}

	/**
	 * Envoi en ligne avec REPLI hors ligne. Avant, un échec réseau (connexion coupée,
	 * serveur momentanément indisponible) levait une erreur : la modification n'était ni
	 * mise en file, ni reproduite dans l'ombre, mais restait en mémoire puis dans la copie
	 * de l'appareil — et disparaissait au rechargement suivant depuis le serveur (réseau
	 * faible, 05-07/10). Désormais elle part par la file d'attente, comme hors ligne.
	 * Une erreur « métier » (4xx, 500) remonte comme avant.
	 */
	private async essayerEnLigne<R>(sync: Boolean, entite: string, appel: () => Promise<R>): Promise<{ fait: boolean; resultat?: R }> {
		if (!sync || !(await firstValueFrom(this.connexionService.isOnline()))) return { fait: false };
		try {
			return { fait: true, resultat: await appel() };
		} catch (erreur: any) {
			const statut = Number(erreur?.status);
			const reseau = statut === 0 || [408, 429, 502, 503, 504].includes(statut);
			if (!reseau) throw erreur;
			console.warn(`⚠️ Envoi ${entite} impossible (HTTP ${statut}) : mis en file d'attente`);
			enregistrerErreurTelemetrie(erreur, 'sync.repli_hors_ligne', { 'entite': entite, 'http.status': statut });
			return { fait: false };
		}
	}

	async addEntity(addEntity: T, sync:Boolean = true, snackBar: Boolean = true, removeForeignProperties:boolean = true): Promise<any> {
		const entity = this.assignClass(addEntity);
		// Avoid storing Blob/File in IndexedDB on iOS: strip Photo.file before local persistence
		this.stripBinaryFieldsIfPhoto(entity as any);
		this.sanitizeForIndexedDb(entity as any);
		const entityName =  this.entityName;
		if (this.isValidEntity(entity, true)) {
			let copyEntity = entity;
			if(removeForeignProperties) {
				copyEntity = this.removeForeignProperties(entity);
			}
			
			const enLigne = await this.essayerEnLigne(sync, entityName, () => { this.entityName = entityName; return this.post(copyEntity); });
			this.entityName = entityName;
			if(enLigne.fait) {
				const response = enLigne.resultat;
				await this.refleterOmbre(entityName, 'add', copyEntity);

				if(snackBar) {
					this._snackBar.open('Ajout enregistré', 'Fermer', {
						duration: 2000,
					});
				}

				return response;
			} else {
				// Always write to waitingtransaction for sync
				console.time(`⏱️ waitingtransaction.add [${entityName}]`);
				console.log('📦 Data size to insert:', JSON.stringify(copyEntity).length, 'bytes');
				console.log('📋 Keys in copyEntity:', Object.keys(copyEntity));
				
				// Le dossier est noté pour TOUTES les opérations (il ne l'était que pour les
				// patchs) : c'est ce qui permet de savoir qu'un dossier a des modifications en
				// attente avant de le remplacer par la copie serveur.
				const biIdAjout = await this.getBiIdFromUri();
				await db.table('waitingtransaction').add({
					creeLe: Date.now(),
					data: copyEntity,
					action: 'add',
					entityName: entityName,
					tries: 0,
					biId: biIdAjout > 0 ? biIdAjout : undefined
				});
				await this.refleterOmbre(entityName, 'add', copyEntity, biIdAjout);
				
				console.timeEnd(`⏱️ waitingtransaction.add [${entityName}]`);
				
				// Only write to IndexedDB if skipIndexedDbIfMissing is false (default behavior)
				const dbentity: string = entityName.toLowerCase();
				let response = null;
				
				if (!this.skipIndexedDbIfMissing) {
					response = await db.table(dbentity).add(this.versStockagePhoto(entity));
				} else {
					console.log(`Skipped IndexedDB write for redundant table: ${dbentity}`);
				}
				
				if(snackBar) {
					this._snackBar.open('Ajout enregistré', 'Fermer', {
						duration: 2000,
					});
				}

				return response;
			}
		} else {
			const error = new Error('Invalid entity');
			error.message = this.getEntityError(entity, true);
			if(snackBar) {
				this._snackBar.open('Erreur lors de l\'ajout', 'Fermer', {
					duration: 2000,
				});
			}
			throw error;
		}
	}

	async localAddEntity(addEntity: T, snackBar: Boolean = true): Promise<any> {
		const entity = this.assignClass(addEntity);
		this.stripBinaryFieldsIfPhoto(entity as any);
		// Photo : on nettoie et stocke la copie allégée (voir versStockagePhoto), pas le
		// graphe complet du dossier.
		const aStocker = this.versStockagePhoto(entity);
		this.sanitizeForIndexedDb(aStocker as any);
		if (this.isValidEntity(entity, true)) {
			const dbentity: string = this.entityName.toLowerCase();
			let response = null;
			
			if (!this.skipIndexedDbIfMissing) {
				response = await db.table(dbentity).add(aStocker);
			} else {
				console.log(`Skipped IndexedDB write for redundant table: ${dbentity}`);
			}
			// Photos : créées localement puis envoyées par l'upload, sans passer par la file.
			if (this.entityName === 'Photo') {
				await this.refleterOmbre('Photo', 'add', entity);
			}
			
			if(snackBar) {
				this._snackBar.open('Ajout enregistré', 'Fermer', {
					duration: 2000,
				});
			}
			return response;
		} else {
			const error = new Error('Invalid entity');
			error.message = this.getEntityError(entity, true);
			if(snackBar) {
				this._snackBar.open('Erreur lors de l\'ajout', 'Fermer', {
					duration: 2000,
				});
			}
			throw error;
		}
	}

	async bulkAddEntities(addEntities: T[], sync:Boolean = true, snackBar: Boolean = true, removeForeignProperties:boolean = true): Promise<any> {
		const entities = addEntities.map((entity) => this.assignClass(entity));
		const entityName =  this.entityName;
		if (entities.every((entity) => this.isValidEntity(entity, true))) {
			let copyEntities = entities;
			if(removeForeignProperties) {
				copyEntities = entities.map((entity) => this.removeForeignProperties(entity));
			}
			
			const enLigne = await this.essayerEnLigne(sync, entityName, () => { this.entityName = entityName; return this.bulkPost(copyEntities); });
			this.entityName = entityName;
			if(enLigne.fait) {
				const response = enLigne.resultat;
				await this.refleterOmbre(entityName, 'bulkAdd', copyEntities);

				if(snackBar) {
					this._snackBar.open('Ajouts enregistrés', 'Fermer', {
						duration: 2000,
					});
				}

				return response;
			} else {
				// Always write to waitingtransaction for sync
				const biIdLot = await this.getBiIdFromUri();
				await db.table('waitingtransaction').add({
					creeLe: Date.now(),
					data: copyEntities,
					action: 'bulkAdd',
					entityName: entityName,
					tries: 0,
					biId: biIdLot > 0 ? biIdLot : undefined
				});
				await this.refleterOmbre(entityName, 'bulkAdd', copyEntities, biIdLot);
				
				// Only write to IndexedDB if skipIndexedDbIfMissing is false (default behavior)
				const dbentity: string = entityName.toLowerCase();
				let response = null;
				
				if (!this.skipIndexedDbIfMissing) {
					response = await db.table(dbentity).bulkAdd(copyEntities);
				} else {
					console.log(`Skipped IndexedDB write for redundant table: ${dbentity}`);
				}
				
				if(snackBar) {
					this._snackBar.open('Ajouts enregistrés', 'Fermer', {
						duration: 2000,
					});
				}

				return response;
			}
		} else {
			const error = new Error('Invalid entity');
			error.message = this.getEntityError(entities[0], true);
			if(snackBar) {
				this._snackBar.open('Erreur lors de l\'ajout', 'Fermer', {
					duration: 2000,
				});
			}
			throw error;
		}		
	}

	async updateEntity(updateEntity: T, sync:Boolean = true, snackBar: Boolean = true, removeForeignProperties:boolean = true): Promise<void> {
		//On force le typage de l'entity
		const entity = this.assignClass(updateEntity);
		this.stripBinaryFieldsIfPhoto(entity as any);
		this.sanitizeForIndexedDb(entity as any);
		const entityName =  this.entityName;
		if (this.isValidEntity(entity)) {
			let copyEntity = Object.assign({}, entity);	
			if(removeForeignProperties) {
				copyEntity = this.removeForeignProperties(entity);
			}
			const enLigne = await this.essayerEnLigne(sync, entityName, () => { this.entityName = entityName; return this.put(copyEntity); });
			this.entityName = entityName;
			if(enLigne.fait) {
				await this.refleterOmbre(entityName, 'update', copyEntity);
				if(snackBar) {
					this._snackBar.open('Modification enregistrée', 'Fermer', {
						duration: 2000,
					});
				}
			}
			else {
				// Always write to waitingtransaction for sync
				const biIdMaj = await this.getBiIdFromUri();
				await db.table('waitingtransaction').add({
					creeLe: Date.now(),
					data: copyEntity,
					action: 'update',
					entityName: entityName,
					tries: 0,
					biId: biIdMaj > 0 ? biIdMaj : undefined
				});
				await this.refleterOmbre(entityName, 'update', copyEntity, biIdMaj);
				
				// Only write to IndexedDB if skipIndexedDbIfMissing is false (default behavior)
				const dbentity: string = entityName.toLowerCase();
				if (!this.skipIndexedDbIfMissing) {
					await db.table(dbentity).put(this.versStockagePhoto(entity));
				} else {
					console.log(`Skipped IndexedDB write for redundant table: ${dbentity}`);
				}

				if(snackBar) {
					this._snackBar.open('Modification enregistrée', 'Fermer', {
						duration: 2000,
					});
				}
			}
		} else {
			const error = new Error('Invalid entity');
			error.message = this.getEntityError(entity);
			if(snackBar) {
				this._snackBar.open('Erreur lors de la modification', 'Fermer', {
					duration: 2000,
				});
			}
			throw error;
		}
	}

	async localUpdateEntity(updateEntity: T, snackBar: Boolean = false): Promise<void> {
		const entity = this.assignClass(updateEntity);
		this.stripBinaryFieldsIfPhoto(entity as any);
		const aStocker = this.versStockagePhoto(entity);
		this.sanitizeForIndexedDb(aStocker as any);
		if (this.isValidEntity(entity)) {
			const dbentity: string = this.entityName.toLowerCase();
			
			if (!this.skipIndexedDbIfMissing) {
				await db.table(dbentity).put(aStocker);
			} else {
				console.log(`Skipped IndexedDB write for redundant table: ${dbentity}`);
			}
			if (this.entityName === 'Photo') {
				await this.refleterOmbre('Photo', 'update', entity);
			}
			
			if(snackBar) {
				this._snackBar.open('Modification enregistrée', 'Fermer', {
					duration: 2000,
				});
			}
		} else {
			const error = new Error('Invalid entity');
			error.message = this.getEntityError(entity);
			if(snackBar) {
				this._snackBar.open('Erreur lors de la modification', 'Fermer', {
					duration: 2000,
				});
			}
			throw error;
		}
	}

	/**
	 * Reproduit l'opération dans le stockage unitaire « ombre » (phase 1, sans effet sur
	 * le fonctionnement : voir services/core/ombre-dossier.ts). Ne lève jamais.
	 */
	private async refleterOmbre(entityName: string, action: ActionOmbre, donnees: any, biId?: number): Promise<void> {
		try {
			if (!ombreDossier.suit(entityName)) return;
			ombreDossier.appliquer(entityName, action, donnees, biId ?? await this.getBiIdFromUri());
		} catch (erreur) {
			console.warn('⚠️ [ombre] opération non reproduite :', erreur);
		}
	}

	async getBiIdFromUri(): Promise<number> {
        try {
            // Méthode 1: Depuis l'URL directement
            const uri = window.location.href;
            console.log('Current URI:', uri);
            
            // Pattern pour matcher les routes avec biId
            // Exemple: /dashboard/rapport/edit/123 ou /rapport/123
            const biIdMatch = uri.match(/\/(?:rapport\/edit|dashboard\/rapport\/edit)\/(\d+)/);
            if (biIdMatch && biIdMatch[1]) {
                const biId = parseInt(biIdMatch[1], 10);
                console.log('BI ID trouvé dans URL:', biId);
                return biId;
            }

            // Méthode 2: Depuis les paramètres de route Angular
            let currentRoute = this.activatedRoute;
            
            // Remonter jusqu'à la route racine pour trouver les paramètres
            while (currentRoute.parent) {
                currentRoute = currentRoute.parent;
            }
            
            // Chercher le paramètre biId dans toute la hiérarchie de routes
            const findBiIdInRoute = (route: ActivatedRoute): number | null => {
                // Vérifier les paramètres de cette route
                if (route.snapshot.params['id']) {
                    const id = parseInt(route.snapshot.params['id'], 10);
                    if (!isNaN(id)) {
                        return id;
                    }
                }
                
                // Vérifier les enfants
                for (const child of route.children) {
                    const childId = findBiIdInRoute(child);
                    if (childId !== null) {
                        return childId;
                    }
                }
                
                return null;
            };
            
            const routeBiId = findBiIdInRoute(currentRoute);
            if (routeBiId !== null) {
                console.log('BI ID trouvé dans route params:', routeBiId);
                return routeBiId;
            }

            // Méthode 3: Pattern matching plus flexible sur l'URL
            const urlSegments = uri.split('/');
            for (let i = 0; i < urlSegments.length; i++) {
                const segment = urlSegments[i];
                
                // Si on trouve 'rapport' ou 'edit', le segment suivant pourrait être l'ID
                if ((segment === 'rapport' || segment === 'edit') && i + 1 < urlSegments.length) {
                    const potentialId = parseInt(urlSegments[i + 1], 10);
                    if (!isNaN(potentialId) && potentialId > 0) {
                        console.log('BI ID trouvé par analyse des segments:', potentialId);
                        return potentialId;
                    }
                }
            }

            console.log('Aucun BI ID trouvé dans l\'URL');
            return 0;
            
        } catch (error) {
            console.error('Erreur lors de la récupération du BI ID depuis l\'URI:', error);
            return 0;
        }
    }

	/**
	 * Un patch n'emporte QUE les champs fournis par l'appelant. assignClass part d'une
	 * instance de la classe, dont les champs initialisés s'ajoutaient à tous les patchs :
	 * pour Photo, `snapshot: ''` (et `parentId: null`, `update: false`). Chaque
	 * modification de rang, déplacement, association… effaçait ainsi côté serveur l'état
	 * d'une annotation (les tracés à rouvrir) — 1,8 % des photos annotées touchées (07/10).
	 */
	private seulementLesChampsFournis<E>(entity: E, fourni: any): E {
		if (!fourni || typeof fourni !== 'object') return entity;
		for (const cle of Object.keys(entity as any)) {
			if (!Object.prototype.hasOwnProperty.call(fourni, cle)) delete (entity as any)[cle];
		}
		return entity;
	}

	async bulkPatchEntities(patchEntities: any[], sync:Boolean = true, snackBar: Boolean = true): Promise<void> {
		const entities = patchEntities.map((entity) => this.seulementLesChampsFournis(this.assignClass(entity), entity));
		const entityName =  this.entityName;
		const enLigne = await this.essayerEnLigne(sync, entityName, () => { this.entityName = entityName; return this.bulkPatch(entities); });
		this.entityName = entityName;
		if(enLigne.fait) {
			await this.refleterOmbre(entityName, 'bulkPatch', entities);
			if(snackBar) {
				this._snackBar.open('Modifications enregistrées', 'Fermer', {
					duration: 2000,
				});
			}
		}
		else {
			console.log('Offline bulk patch, storing in waitingtransaction');
			console.log(entities, entityName);
			const biId = await this.getBiIdFromUri();

			await db.table('waitingtransaction').add({
				creeLe: Date.now(),
				data: entities,
				action: 'bulkPatch',
				entityName: entityName,
				biId: biId > 0 ? biId : undefined,
				tries: 0
			});
			await this.refleterOmbre(entityName, 'bulkPatch', entities, biId);
			this.entityName = entityName;
			// await this.bulkPut(entities);
			// for(const entity of entities) {
			// 	await this.localPatchEntity(entity, false);
			// }
			
			// Only write to IndexedDB if skipIndexedDbIfMissing is false (default behavior)
			const dbentity: string = entityName.toLowerCase();
			if (!this.skipIndexedDbIfMissing) {
				// Mise à jour PARTIELLE, comme un patch. bulkPut REMPLAÇAIT chaque ligne par
				// l'objet passé : la galerie envoie { id, sort } pour ordonner les photos, et la
				// photo perdait isSync:false (sortie de la file d'envoi pour de bon), son
				// rapport, son nom de fichier… Une ligne absente reste absente (pas de
				// création d'une ligne partielle).
				await db.table(dbentity).bulkUpdate(
					entities.filter((e: any) => e?.id != null).map((e: any) => ({ key: e.id, changes: e }))
				);
			} else {
				console.log(`Skipped IndexedDB write for redundant table: ${dbentity}`);
			}
			
			if(snackBar) {
				this._snackBar.open('Modifications enregistrées', 'Fermer', {
					duration: 2000,
				});
			}
		}
	}

	async patchEntity(patchEntity: any, sync:Boolean = true, snackBar: Boolean = true): Promise<void> {
		const entity = this.seulementLesChampsFournis(this.assignClass(patchEntity), patchEntity);
		const entityName =  this.entityName;
		const enLigne = await this.essayerEnLigne(sync, entityName, () => { this.entityName = entityName; return this.patch(entity); });
		this.entityName = entityName;
		if(enLigne.fait) {
			await this.refleterOmbre(entityName, 'patch', entity);
			if(snackBar) {
				this._snackBar.open('Modification enregistrée', 'Fermer', {
					duration: 2000,
				});
			}
		}
		else {
			console.log('Offline patch, storing in waitingtransaction');
			console.log(entity, entityName);
			this.getBiIdFromUri();
			const biId = await this.getBiIdFromUri();
			await db.table('waitingtransaction').add({
				creeLe: Date.now(),
				data: entity,
				action: 'patch',
				entityName: entityName,
				tries: 0,
				biId: biId > 0 ? biId : undefined
			});
			await this.refleterOmbre(entityName, 'patch', entity, biId);
			this.entityName = entityName;
			await this.localPatchEntity(entity, snackBar);
		}
	}
	
	async localPatchEntity(entity: any, snackBar: Boolean = false): Promise<void> {
		const dbentity: string = this.entityName.toLowerCase();
		// Avoid storing Blob/File or typed arrays anywhere in the object graph
		this.stripBinaryFieldsIfPhoto(entity as any);
		this.sanitizeForIndexedDb(entity as any);
		
		if (!this.skipIndexedDbIfMissing) {
			await db.table(dbentity).update(entity.id, entity);
		} else {
			console.log(`Skipped IndexedDB write for redundant table: ${dbentity}`);
		}
		
		if(snackBar) {
			this._snackBar.open('Modification enregistrée', 'Fermer', {
				duration: 2000,
			});
		}
	}

	async deleteEntity(id: number|string, sync: Boolean = true, snackBar: Boolean = true): Promise<void> {
		const entityNameSuppr = this.entityName;
		const enLigne = await this.essayerEnLigne(sync, entityNameSuppr, () => this.delete(id));
		if(enLigne.fait) {
			await this.refleterOmbre(entityNameSuppr, 'delete', id);
			if(snackBar) {
				this._snackBar.open('Suppression enregistrée', 'Fermer', {
					duration: 2000,
				});
			}
		}
		else {
			// if(sync) {
				const biIdSuppr = await this.getBiIdFromUri();
				await db.table('waitingtransaction').add({
					creeLe: Date.now(),
					data: id,
					action: 'delete',
					entityName: this.entityName,
					tries: 0,
					biId: biIdSuppr > 0 ? biIdSuppr : undefined
				});
				await this.refleterOmbre(entityNameSuppr, 'delete', id, biIdSuppr);
			// }
			await this.localDeleteEntity(id, snackBar);
		}
	}

	async bulkDeleteEntities(ids: (number|string)[], sync: Boolean = true, snackBar: Boolean = true): Promise<void> {
		const entityNameSuppr = this.entityName;
		const enLigne = await this.essayerEnLigne(sync, entityNameSuppr, () => this.bulkDelete(ids));
		if(enLigne.fait) {
			await this.refleterOmbre(entityNameSuppr, 'bulkDelete', ids);
			if(snackBar) {
				this._snackBar.open('Suppressions enregistrées', 'Fermer', {
					duration: 2000,
				});
			}
		}
		else {
			// Always write to waitingtransaction for sync
			const biIdSupprLot = await this.getBiIdFromUri();
			await db.table('waitingtransaction').add({
				creeLe: Date.now(),
				data: ids,
				action: 'bulkDelete',
				entityName: this.entityName,
				tries: 0,
				biId: biIdSupprLot > 0 ? biIdSupprLot : undefined
			});
			await this.refleterOmbre(entityNameSuppr, 'bulkDelete', ids, biIdSupprLot);

			// Only write to IndexedDB if skipIndexedDbIfMissing is false (default behavior)
			const dbentity: string = this.entityName.toLowerCase();
			if (!this.skipIndexedDbIfMissing) {
				const response = await db.table(dbentity).bulkDelete(ids);
			} else {
				console.log(`Skipped IndexedDB write for redundant table: ${dbentity}`);
			}
			
			// for(const id of ids) {
			// 	await this.localDeleteEntity(id, false);
			// }
			if(snackBar) {
				this._snackBar.open('Suppressions enregistrées', 'Fermer', {
					duration: 2000,
				});
			}
		}
	}

	async localDeleteEntity(id: number|string, snackBar: Boolean = false): Promise<void> {
		const dbentity: string = this.entityName.toLowerCase();
		
		if (!this.skipIndexedDbIfMissing) {
			await db.table(dbentity).delete(id);
		} else {
			console.log(`Skipped IndexedDB write for redundant table: ${dbentity}`);
		}
		
		if(snackBar) {
			this._snackBar.open('Suppression enregistrée', 'Fermer', {
				duration: 2000,
			});
		}
	}

	async cacheEntity(id: number|string) {
		return new Promise((resolve, reject) => {
			this.http.put(environment.baseUrl+'sync/cache/'+this.entityName, {
				id: id
			}).subscribe({
				next: (response) => {
					resolve(response);
				},
				error: (error) => {
					// Ensure rejection in case of subscription error
					reject(error);
				}
			});
		});
	}

	async post(entity: T) {
		return new Promise((resolve, reject) => {
			this.http.post(environment.baseUrl+'sync/'+this.entityName, {
				entity: entity
			}).subscribe({
				next: (response) => {
					resolve(response);
				},
				error: (error) => {
					// Ensure rejection in case of subscription error
					reject(error);
				}
			});
		});
	}

	async bulkPost(entities: T[]) {
		return new Promise((resolve, reject) => {
			this.http.post(environment.baseUrl+'sync/bulk/'+this.entityName, {
				entities: entities
			}).subscribe({
				next: (response) => {
					resolve(response);
				},
				error: (error) => {
					// Ensure rejection in case of subscription error
					reject(error);
				}
			});
		});
	}

	async bulkPatch(entities: T[]) {
		return new Promise((resolve, reject) => {
			this.http.patch(environment.baseUrl+'sync/bulk/'+this.entityName, {
				entities: entities
			}).subscribe({
				next: (response) => {
					resolve(response);
				},
				error: (error) => {
					// Ensure rejection in case of subscription error
					reject(error);
				}
			});
		});
	}

	async bulkDelete(ids: (number|string)[]) {
		return new Promise((resolve, reject) => {
			this.http.request('delete', environment.baseUrl+'sync/bulk/'+this.entityName, {
				body: {
					ids: ids
				}
			}).subscribe({
				next: (response) => {
					resolve(response);
				},
				error: (error) => {
					// Ensure rejection in case of subscription error
					reject(error);
				}
			});
		});
	}

	async put(entity: T) {
		return new Promise((resolve, reject) => {
			this.http.put(environment.baseUrl+'sync/'+this.entityName, {
				entity: entity
			}).subscribe({
				next: (response) => {
					resolve(response);
				},
				error: (error) => {
					// Ensure rejection in case of subscription error
					reject(error);
				}
			});
		});
	}

	async patch(entity: T) {
		return new Promise((resolve, reject) => {
			this.http.patch(environment.baseUrl+'sync/'+this.entityName, {
				entity: entity
			}).subscribe({
				next: (response) => {
					resolve(response);
				},
				error: (error) => {
					// Ensure rejection in case of subscription error
					reject(error);
				}
			});
		});
	}

	async delete(id: number|string) {
		return new Promise((resolve, reject) => {
			this.http.delete(environment.baseUrl+'sync/'+this.entityName+'/'+id).subscribe({
				next: (response) => {
					resolve(response);
				},
				error: (error) => {
					// Ensure rejection in case of subscription error
					reject(error);
				}
			});
		});
	}

	async get(id: number, sync: boolean = true): Promise<T> {
		if(sync && await firstValueFrom(this.connexionService.isOnline())) {
			const syncResult = await this.sync(id);
			if (this.skipIndexedDbIfMissing && syncResult && syncResult !== true && !Array.isArray(syncResult)) {
				return this.assignClass(syncResult as T);
			}
		}
		const dbentity: string = this.entityName.toLowerCase();
		return await db.table(dbentity).get(id);
	}

	async filter(filter: any, sync: boolean = true): Promise<T[]> {
		if(sync && await firstValueFrom(this.connexionService.isOnline())) {
			await this.sync();
		}
		const dbentity: string = this.entityName.toLowerCase();
		return await db.table(dbentity).filter((T) => {
			return Object.keys(filter).every((key) => {
				// console.log(key, filter[key], T[key]);
				if(Array.isArray(filter[key])) {
					return filter[key].includes(T[key]);
				}
				return T[key] === filter[key];
			});
		}).toArray();
	}

	async sync(id:number = 0, minimal:boolean = false): Promise<boolean | T | T[]> {
		return new Promise((resolve, reject) => {
			const dbentity: string = this.entityName.toLowerCase();
			console.log("sync", id);
			if(id == 0) {
				// Le contenu local est remplacé APRÈS réception, en une seule transaction. Avant,
				// la table était vidée AVANT la requête : sur un réseau saturé (envoi de photos),
				// elle restait vide 20 à 70 s — liste des pièces vide dans le tableau de dégâts
				// (Xavier Charraud, 29/09). Et sans gestion d'erreur, un échec réseau laissait
				// l'appelant attendre indéfiniment : on garde alors les données locales.
				const table = dbentity;
				this.http.get(environment.baseUrl+'sync/'+this.entityName+'/'+(minimal?1:0)).subscribe({
					next: async (response) => {
						const entities = response as T[];
						if (!this.skipIndexedDbIfMissing) {
							try {
								await db.transaction('rw', db.table(table), async () => {
									await db.table(table).clear();
									await db.table(table).bulkPut(entities);
								});
							} catch (erreur) {
								console.warn(`⚠️ Synchro ${table} non enregistrée, données locales conservées :`, erreur);
							}
							resolve(true);
						} else {
							// Retourner directement les données de l'API
							resolve(entities);
						}
					},
					error: (erreur) => {
						console.warn(`⚠️ Synchro ${table} impossible, données locales conservées :`, erreur);
						resolve(false);
					},
				});
			}
			else {
				console.log("sync", environment.baseUrl+'sync/'+this.entityName+'/'+(minimal?1:0));
				const debutRequete = Date.now(); // ombre : instant de la copie serveur
				this.http.get(environment.baseUrl+'sync/'+this.entityName+'/'+(minimal?1:0)+'/'+id).subscribe(async (response) => {
					if (!this.skipIndexedDbIfMissing) {
						const dbentity: string = this.entityName.toLowerCase();
						const bi: any = response;
						const proteger = this.entityName === 'Bi' && bi?.id != null
							&& (await this.dossiersAvecModificationsEnAttente()).has(Number(bi.id));
						if (proteger) {
							console.log(`🛡️ Dossier ${bi.id} : modifications en attente, copie locale conservée`);
						} else {
							await db.table(dbentity).put(response as T);
						}
						// L'ombre suit le monolithe : ressemée seulement si la copie serveur y a été écrite.
						if (this.entityName === 'Bi' && !minimal && !proteger) ombreDossier.semerDepuisServeur([response], debutRequete);
						resolve(true);
					} else {
						resolve(response as T);
					}
				});
			}
					
		});
	}

	async syncFilter(filter: any, minimal:boolean = false): Promise<T[]> {
		return new Promise((resolve, reject) => {
			const debutRequete = Date.now(); // ombre : instant de la copie serveur
			this.http.post(environment.baseUrl+'sync/'+this.entityName+'/'+(minimal?1:0), {
				params: filter
			}).subscribe(async (response) => {
				// Utiliser bulkPut au lieu de put en boucle pour de meilleures performances
				if (!this.skipIndexedDbIfMissing) {
					const dbentity: string = this.entityName.toLowerCase();
					let aEcrire = response as any[];
					if (this.entityName === 'Bi' && Array.isArray(aEcrire)) {
						const enAttente = await this.dossiersAvecModificationsEnAttente();
						if (enAttente.size > 0) {
							aEcrire = aEcrire.filter(bi => !enAttente.has(Number(bi?.id)));
						}
					}
					await db.table(dbentity).bulkPut(aEcrire as T[]);
					// L'ombre suit le monolithe : seulement les dossiers réellement écrits.
					if (this.entityName === 'Bi' && !minimal) ombreDossier.semerDepuisServeur(aEcrire as any[], debutRequete);
				}
				resolve(response as T[]);
			}, (error) => {
				reject(error);
			});
		});
	}

	private isValidEntity(entity: any, create: boolean = false): entity is T {
		const requiredProperties = [...(entity.constructor as any).requiredProperties || []];
		const primaryKey = (entity.constructor as any).primaryKey;
		if(create) {
			requiredProperties.splice(requiredProperties.indexOf(primaryKey), 1);
		}
		return requiredProperties.every((key: string) => key in entity && typeof entity[key] !== 'undefined');
	}

	private getEntityError(entity: any, create: boolean = false) {
		const requiredProperties = [...(entity.constructor as any).requiredProperties || []];
		const primaryKey = (entity.constructor as any).primaryKey;
		if(create) {
			requiredProperties.splice(requiredProperties.indexOf(primaryKey), 1);
		}
		if(!requiredProperties.every((key: string) => key in entity && typeof entity[key] !== 'undefined')) {
			const missingProperties = requiredProperties.filter((key: string) => !(key in entity) || typeof entity[key] === 'undefined');
			if (missingProperties.length > 0) {
				return `Missing required properties: ${missingProperties.join(', ')}`;
			}
		}
		return "Unknown error";
	}

	private removeForeignProperties(entity: any) {
		const copyEntity = {...entity};
		const keepProperties = [...(entity.constructor as any).keepProperties || []];
		
		for(const key in entity) {
			if(keepProperties.includes(key)) {
				continue;
			}

			if(entity[key] instanceof Object && !Array.isArray(entity[key])) {
				copyEntity[key] = this.removeForeignProperties(entity[key]);
			}
			else if(entity[key] instanceof Array) {
				delete copyEntity[key];
			}
		}

		return copyEntity;
	}

	// Helper: strip File/Blob from Photo entities to avoid IndexedDB ModifyError on iOS
	/**
	 * Copie d'une photo pour la table LOCALE `photo` : chaque relation (rapport, phrase,
	 * question, investigation, parent) réduite à son identifiant. L'objet en mémoire n'est
	 * pas modifié.
	 *
	 * La photo portait `rapport = bi.rapport` — tout le dossier, phrases et définitions
	 * comprises — sérialisé à CHAQUE photo enregistrée : médiane 4,3 s par photo sur les
	 * tablettes, jusqu'à 245 s (télémétrie 2.1.39, 492 cas en 2 jours). Les lecteurs de
	 * cette table (envoi simple et par lot) n'utilisent que les identifiants.
	 */
	private versStockagePhoto(entity: any): any {
		if (this.entityName !== 'Photo' || !entity || typeof entity !== 'object') return entity;
		const copie: any = { ...entity };
		for (const cle of ['rapport', 'phraseRapport', 'question', 'investigation', 'parent']) {
			const valeur = copie[cle];
			if (valeur && typeof valeur === 'object') {
				copie[cle] = valeur.id != null ? { id: valeur.id } : null;
			}
		}
		return copie;
	}

	private stripBinaryFieldsIfPhoto(entity: any) {
		try {
			// entityName may not be set on some direct calls; also check shape
			const looksLikePhoto = (this.entityName === 'Photo') || (entity && typeof entity === 'object' && 'fileName' in entity && 'type' in entity && ('file' in entity || 'url' in entity));
			if (!looksLikePhoto) return;
			// Preserve a base64 url preview if present; drop the File/Blob to keep Dexie happy
			if ('file' in entity) {
				try { delete entity.file; } catch {}
			}
			// Ensure an url string exists; upstream code sets it for previews/uploads
		} catch {}
	}

	// Deep sanitization: remove Blob/File/ArrayBuffer/TypedArray from any nested property before writing to IndexedDB
	private sanitizeForIndexedDb(input: any, seen: WeakSet<object> = new WeakSet()): any {
		const isObject = (v: any) => v && typeof v === 'object';
		const isBinary = (v: any) => (
			typeof File !== 'undefined' && v instanceof File
		) || (
			typeof Blob !== 'undefined' && v instanceof Blob
		) || (
			v instanceof ArrayBuffer
		) || (
			ArrayBuffer && ArrayBuffer.isView && ArrayBuffer.isView(v)
		);

		const cleanse = (value: any): any => {
			if (!isObject(value)) return value;
			if (seen.has(value)) return value;
			seen.add(value);

			if (isBinary(value)) return undefined;

			if (Array.isArray(value)) {
				for (let i = 0; i < value.length; i++) {
					const sanitized = cleanse(value[i]);
					if (sanitized === undefined) {
						// Remove binary entries
						value.splice(i, 1);
						i--;
					} else {
						value[i] = sanitized;
					}
				}
				return value;
			}

			// Plain object
			for (const key of Object.keys(value)) {
				const val = (value as any)[key];
				if (key === 'file' && isBinary(val)) {
					delete (value as any)[key];
					continue;
				}
				const sanitized = cleanse(val);
				if (sanitized === undefined) delete (value as any)[key];
				else (value as any)[key] = sanitized;
			}
			return value;
		};

		return cleanse(input);
	}
}